import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Globe, IndianRupee, Info } from 'lucide-react';

export const FX_CURRENCIES = ['USD', 'EUR', 'GBP', 'SGD', 'AED', 'JPY'];

/** Months between now and an ISO date (approx). */
function monthsOut(iso) {
  if (!iso) return 0;
  const d = new Date(iso);
  if (isNaN(d)) return 0;
  return (d.getFullYear() - new Date().getFullYear()) * 12 + (d.getMonth() - new Date().getMonth());
}

/**
 * BATCH 2C — Quote Currency Mode selector.
 * INR (default) = client price fixed in INR. FX = client price fixed in a
 * foreign currency; INR is only an estimate here (realised per-payment).
 * The FX option is shown ONLY to Admin/Accounts (canFx). Agents never see it.
 */
export default function QuoteCurrencyMode({ value, onChange, canFx, travelStartDate }) {
  const mode = (value?.quoteCurrencyMode || 'INR').toUpperCase();
  const cur = value?.quoteCurrency || 'USD';
  const amt = value?.fxClientAmount ?? '';
  const rate = value?.fxIndicativeRate ?? '';
  const rateDate = value?.fxRateDate || '';
  const indicativeInr = (Number(amt) || 0) * (Number(rate) || 0);
  const farOut = monthsOut(travelStartDate) >= 4;

  // Agents can only ever quote in INR — no selector, no FX affordance.
  if (!canFx) return null;

  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="quote-currency-mode">
      <div className="flex items-center gap-2">
        <Globe className="h-4 w-4 text-teal-600" />
        <Label className="text-sm font-semibold">Quote Currency Mode</Label>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          data-testid="quote-mode-inr"
          onClick={() => set({ quoteCurrencyMode: 'INR' })}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors ${mode === 'INR' ? 'border-teal-500 bg-teal-50 text-teal-800 font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
        >
          <IndianRupee className="h-4 w-4" /> INR (default)
        </button>
        <button
          type="button"
          data-testid="quote-mode-fx"
          onClick={() => set({ quoteCurrencyMode: 'FX', quoteCurrency: cur })}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors ${mode === 'FX' ? 'border-indigo-500 bg-indigo-50 text-indigo-800 font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
        >
          <Globe className="h-4 w-4" /> Foreign Currency
        </button>
      </div>

      {mode === 'FX' && (
        <div className="space-y-3 pt-1" data-testid="quote-fx-fields">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Currency</Label>
              <Select value={cur} onValueChange={(v) => set({ quoteCurrency: v })}>
                <SelectTrigger className="mt-1" data-testid="quote-fx-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FX_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Client price ({cur})</Label>
              <Input type="number" min="0" value={amt} onChange={(e) => set({ fxClientAmount: e.target.value })} className="mt-1" data-testid="quote-fx-amount" placeholder="2400" />
            </div>
            <div>
              <Label className="text-xs">Indicative rate (₹/{cur})</Label>
              <Input type="number" min="0" value={rate} onChange={(e) => set({ fxIndicativeRate: e.target.value })} className="mt-1" data-testid="quote-fx-rate" placeholder="86.50" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Rate date</Label>
            <Input type="date" value={rateDate} onChange={(e) => set({ fxRateDate: e.target.value })} className="mt-1 w-48" data-testid="quote-fx-ratedate" />
          </div>
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="quote-fx-estimate">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              Client pays a fixed <strong>{cur} {Number(amt || 0).toLocaleString()}</strong>. Indicative INR ≈
              <strong> ₹{Math.round(indicativeInr).toLocaleString('en-IN')}</strong> — <em>estimate only, not the amount collected</em>. The real INR is set at each payment using that day's rate.
            </span>
          </div>
        </div>
      )}

      {mode === 'INR' && farOut && (
        <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="quote-fx-validity-note">
          <Info className="h-3.5 w-3.5" /> FX rate{rateDate ? ` as of ${rateDate}` : ''}, subject to revision (trip is 4+ months out).
        </p>
      )}
    </div>
  );
}
